import { Routes } from '@angular/router';
import { AppLayout } from '@/layout/components/app.layout';
import { AuthGuard } from '@/guards/auth.guard';

export const appRoutes: Routes = [
    // Redirect root to login
    { path: '', redirectTo: '/login', pathMatch: 'full' },

    // Login route (no layout)
    { path: 'login', loadComponent: () => import('@/pages/auth/mpi-login').then(c => c.MpiLogin) },
    { path: 'register', loadComponent: () => import('@/pages/auth/register/register').then(c => c.Register) },
    { path: 'forgotpassword', loadComponent: () => import('@/pages/auth/forgotpassword').then(c => c.ForgotPassword) },
    { path: 'reset-password', loadComponent: () => import('@/pages/auth/newpassword').then(c => c.NewPassword) },
    { path: 'new-account', loadComponent: () => import('@/pages/new-account/new-account.component').then(c => c.NewAccountComponent) },
    { path: 'link-account', loadComponent: () => import('@/pages/link-account/link-account.component').then(c => c.LinkAccountComponent) },
    { path: 'verify-account', loadComponent: () => import('@/pages/auth/email-verification').then(c => c.EmailVerification) },
    { path: 'logout', loadComponent: () => import('@/pages/auth/logout').then(c => c.Logout) },
    { path: 'unsubscribe', loadComponent: () => import('@/pages/unsubscribe/unsubscribe.component').then(c => c.UnsubscribeComponent), title: 'Unsubscribe from Email Notifications' },
    {
        path: '',
        component: AppLayout,
        children: [
            {
                path: 'tenant',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_TENANT' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@/pages/dashboards/tenant-dashboard/tenant-dashboard').then(c => c.TenantDashboard),
                        title: "Tenant Dashboard"
                    }, {
                        path: 'dashboard',
                        loadComponent: () => import('@/pages/dashboards/tenant-dashboard/tenant-dashboard').then(c => c.TenantDashboard),
                        title: "Tenant Dashboard"
                    }, {
                        path: 'manage-payment-methods',
                        loadComponent: () => import('@/pages/manage-payment-methods/manage-payment-methods').then(c => c.ManagePaymentMethods),
                        title: "Manage Payment Methods"
                    }, {
                        path: 'invoice-list',
                        loadComponent: () => import('@/pages/tenant-invoices/tenant-invoices-list').then(c => c.TenantInvoiceList),
                        title: "Invoice List"
                    }, {
                        path: 'lease-documents',
                        loadComponent: () => import('@/pages/tenant-lease-documents/tenant-lease-documents').then(c => c.TenantLeaseDocuments),
                        title: "Lease Documents"
                    }
                ]
            },
            {
                path: 'admin',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_ADMIN' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@/pages/dashboards/admin-dashboard/admin-dashboard').then(c => c.AdminDashboard),
                        canActivate: [AuthGuard],
                        title: "Admin Dashboard"
                    },
                    {
                        path: 'dashboard',
                        loadComponent: () => import('@/pages/dashboards/admin-dashboard/admin-dashboard').then(c => c.AdminDashboard),
                        canActivate: [AuthGuard],
                        title: "Admin Dashboard"
                    }, {
                        path: 'settings',
                        loadComponent: () => import('@/pages/settings/settings.component').then(c => c.SettingsComponent),
                        canActivate: [AuthGuard],
                        title: "Settings"
                    },
                    {
                        path: 'monthly-billing',
                        loadComponent: () => import('@/pages/monthly-billing/monthly-billing').then(c => c.MonthlyBilling),
                        canActivate: [AuthGuard],
                        title: "Monthly Billing"
                    },
                    {
                        path: 'manage-tenants',
                        loadComponent: () => import('@/pages/manage-tenants/manage-tenants').then(c => c.ManageTenants),
                        canActivate: [AuthGuard],
                        title: "Manage Tenants"
                    },
                    {
                        path: 'manage-logins',
                        loadComponent: () => import('@/pages/manage-logins/manage-logins').then(c => c.ManageLogins),
                        canActivate: [AuthGuard],
                        title: "Manage User Logins"
                    },
                    {
                        path: 'manage-leases',
                        loadComponent: () => import('@/pages/leases/manage-leases').then(c => c.ManageLeases),
                        canActivate: [AuthGuard],
                        title: "Manage Leases"
                    },
                    {
                        path: 'manage-suites',
                        loadComponent: () => import('@/pages/suites/manage-suites').then(c => c.ManageSuites),
                        canActivate: [AuthGuard],
                        title: "Manage Suites"
                    },
                    {
                        path: 'invoice-list',
                        loadComponent: () => import('@/pages/invoice-list/invoice-list').then(c => c.InvoiceList),
                        canActivate: [AuthGuard],
                        title: "Invoice List"
                    },
                    {
                        path: 'account-requests',
                        loadComponent: () => import('@/pages/account-requests/account-requests.component').then(c => c.AccountRequestsComponent),
                        canActivate: [AuthGuard],
                        title: "Account Requests"
                    },
                    {
                        path: 'payment-logs',
                        loadComponent: () => import('@/pages/payment-logs/payment-logs.component').then(c => c.PaymentLogsComponent),
                        canActivate: [AuthGuard],
                        title: "Payment Logs"
                    },
                    {
                        path: 'charge-codes',
                        loadComponent: () => import('@/pages/charge-codes/charge-codes').then(c => c.ChargeCodes),
                        canActivate: [AuthGuard],
                        title: "Charge Code Management"
                    },
                    {
                        path: 'receive-payment',
                        loadComponent: () => import('@/pages/receive-payment/receive-payment').then(c => c.ReceivePayment),
                        title: "Receive Payment"
                    },
                    {
                        path: 'invoice-tenant',
                        loadComponent: () => import('@/pages/invoice-tenant/invoice-tenant').then(c => c.InvoiceTenant),
                        canActivate: [AuthGuard],
                        title: "Invoice Tenant"
                    },
                    {
                        path: 'email-logs',
                        loadComponent: () => import('@/pages/email-logs/email-logs.component').then(c => c.EmailLogsComponent),
                        canActivate: [AuthGuard],
                        title: "Email Logs"
                    },
                    {
                        path: 'system-status',
                        loadComponent: () => import('@/pages/admin/system-status/system-status.component').then(c => c.SystemStatusComponent),
                        canActivate: [AuthGuard],
                        title: "System Status"
                    },
                    {
                        path: 'link-tenant-to-ach-deposit-account',
                        loadComponent: () => import('@/pages/link-tenant-to-ach-deposit-account/link-tenant-to-ach-deposit-account.component').then(c => c.LinkTenantToAchDepositAccountComponent),
                        canActivate: [AuthGuard],
                        title: "Link Tenant To ACH Deposit Account"
                    },
                    {
                        path: 'ach-deposit-reconciliation',
                        loadComponent: () => import('@/pages/ach-deposit-reconciliation/ach-deposit-reconciliation.component').then(c => c.AchDepositReconciliationComponent),
                        canActivate: [AuthGuard],
                        title: "ACH Deposit Reconciliation"
                    },
                ]
            },
            {
                path: 'accounting',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_ACCOUNTING' },
                children: [{
                    path: '',
                    loadComponent: () => import('@/pages/dashboards/accounting-dashboard/accounting-dashboard').then(c => c.AccountingDashboard),
                    title: "Accounting Dashboard"
                }, {
                    path: 'dashboard',
                    loadComponent: () => import('@/pages/dashboards/accounting-dashboard/accounting-dashboard').then(c => c.AccountingDashboard),
                    title: "Accounting Dashboard"
                }, {
                    path: 'accounting-activity',
                    loadComponent: () => import('@/pages/accounting-activity/accounting-activity').then(c => c.AccountingActivity),
                    title: "Accounting Activity"
                }, {
                    path: 'charge-codes',
                    loadComponent: () => import('@/pages/charge-codes/charge-codes').then(c => c.ChargeCodes),
                    title: "Charge Code Management"
                }, {
                    path: 'invoice-tenant',
                    loadComponent: () => import('@/pages/invoice-tenant/invoice-tenant').then(c => c.InvoiceTenant),
                    title: "Invoice Tenant"
                }, {
                    path: 'receive-payment',
                    loadComponent: () => import('@/pages/receive-payment/receive-payment').then(c => c.ReceivePayment),
                    title: "Receive Payment"
                }, {
                    path: 'invoice-list',
                    loadComponent: () => import('@/pages/invoice-list/invoice-list').then(c => c.InvoiceList),
                    canActivate: [AuthGuard],
                    title: "Invoice List"
                }, {
                    path: 'email-logs',
                    loadComponent: () => import('@/pages/email-logs/email-logs.component').then(c => c.EmailLogsComponent),
                    canActivate: [AuthGuard],
                    title: "Email Logs"
                }, {
                    path: 'ach-deposit-reconciliation',
                    loadComponent: () => import('@/pages/ach-deposit-reconciliation/ach-deposit-reconciliation.component').then(c => c.AchDepositReconciliationComponent),
                    canActivate: [AuthGuard],
                    title: "ACH Deposit Reconciliation"
                }, {
                    path: 'link-tenant-to-ach-deposit-account',
                    loadComponent: () => import('@/pages/link-tenant-to-ach-deposit-account/link-tenant-to-ach-deposit-account.component').then(c => c.LinkTenantToAchDepositAccountComponent),
                    canActivate: [AuthGuard],
                    title: "Link Tenant To ACH Deposit Account"
                },]
            },
            {
                path: 'dev/view-invoice',
                loadComponent: () => import('@/pages/dev/view-invoice').then(c => c.ViewInvoice),
                canActivate: [AuthGuard]
            },
        ]
    },
    // Auth routes
    { path: 'auth', loadChildren: () => import('@/pages/auth/auth.routes') },
    { path: 'landing', loadComponent: () => import('@/pages/landing/landing').then(c => c.Landing) },
    { path: 'notfound', loadComponent: () => import('@/pages/notfound/notfound').then(c => c.Notfound) },
    { path: '**', redirectTo: '/notfound' }
];
